/* <td-booster-buybox-v2-builder> — each (checked booster set + purchase option)
   resolves to ONE pre-made product (combo JSON); its variant (+ Recharge plan on a
   subscription option) drives the <product-form>.

   V2 vs V1: supply length is its own product, so the combo map is keyed by boosters
   AND frequency. The plan picker is one flat radio group whose values are
   "<plan>:<frequency>" — a subscription option per frequency plus one One-Time
   option — so a card selects the variant and the plan together. */
if (!customElements.get('td-booster-buybox-v2-builder')) {
  const SELECTORS = {
    atcButton: '[data-td-bbb-atc-button]',
    atcComparePrice: '[data-atc-compare-price]',
    atcPrice: '[data-atc-price]',
    autoPrice: '[data-td-bbb-auto-price]',
    basePrice: '[data-td-bbb-base-price]',
    baseWas: '[data-td-bbb-base-was]',
    card: '[data-td-bbb-card]',
    cardShell: '[data-td-bbb-card-shell]',
    checkbox: '[data-td-bbb-checkbox]',
    combos: '[data-td-bbb-combos]',
    form: 'form[data-type="add-to-cart-form"]',
    liveRegion: '[data-td-bbb2-live-region]',
    mainSwiper: '[data-swiper]:not([data-swiper-thumbs])',
    optionInput: '[data-td-bbb2-plan]',
    planCard: '[data-td-bbb2-plan-card]',
    planNow: '[data-td-bbb2-plan-now]',
    planSave: '[data-td-bbb2-plan-save]',
    planSoldOut: '[data-td-bbb2-plan-soldout]',
    planTitle: '.td-bbb2__plan-title',
    planUnit: '[data-td-bbb2-plan-unit]',
    planUnitMoney: '[data-td-bbb2-plan-unit-money]',
    planWas: '[data-td-bbb2-plan-was]',
    previewBoosters: '[data-td-bbb-preview-boosters]',
    sellingPlanInput: 'input[name="selling_plan"]',
    soldOutLabel: '[data-td-bbb-soldout-label]',
    stack: '[data-td-bbb-stack]',
    stackBadge: '[data-td-bbb-stack-badge]',
    stackCount: '[data-td-bbb-stack-count]',
    stackList: '[data-td-bbb-stack-list]',
    stackNow: '[data-td-bbb-stack-now]',
    stackPerMonth: '[data-td-bbb-stack-per-month]',
    stackToggle: '[data-td-bbb-stack-toggle]',
    stackToggleText: '[data-td-bbb-stack-toggle-text]',
    stackWas: '[data-td-bbb-stack-was]',
    variantInput: 'input[name="id"]',
  };

  const SAVINGS_TOKEN = '{savings}';
  const PLAN_TOKEN = '{plan}';
  // Booster art is square; the composite caps each at 30% of the stage.
  const GALLERY_IMAGE_SIZES = '(min-width: 990px) 165px, 95px';
  const GALLERY_IMAGE_WIDTH = 380;
  const GALLERY_IMAGE_HEIGHT = 380;

  /** Resolves booster + frequency selections to their product and purchase plan. */
  class TdBoosterBuyboxV2Builder extends HTMLElement {
    /**
     * Connects controls and resolves the initial stack.
     * @returns {void}
     */
    connectedCallback() {
      this.combos = this.parseCombos();
      this.checkboxes = Array.from(this.querySelectorAll(SELECTORS.checkbox));
      this.form = this.querySelector(SELECTORS.form);
      this.idInput = this.form ? this.form.querySelector(SELECTORS.variantInput) : null;
      this.basePriceEl = this.querySelector(SELECTORS.basePrice);
      this.baseWasEl = this.querySelector(SELECTORS.baseWas);
      this.atcPriceEl = this.querySelector(SELECTORS.atcPrice);
      this.atcCompareEl = this.querySelector(SELECTORS.atcComparePrice);
      this.atcButton = this.querySelector(SELECTORS.atcButton);
      this.boosterContainer = this.querySelector(SELECTORS.previewBoosters);

      // Each option input is wrapped by its own plan card, which carries the
      // benefit list, the sold-out line and the badge.
      this.optionInputs = Array.from(this.querySelectorAll(SELECTORS.optionInput));
      this.optionCards = new Map();
      this.optionInputs.forEach((input) => {
        const card = input.closest(SELECTORS.planCard);
        if (card) this.optionCards.set(input, card);
      });

      // Reference supply length for the static base/booster prices shown in the
      // selector. Falls back to whichever option is the One-Time one.
      this.onetimeFrequency = this.getAttribute('data-td-bbb2-onetime-frequency') || '';
      if (!this.onetimeFrequency) {
        const ot = this.optionInputs.find((input) => this.optionOf(input).plan === 'onetime');
        this.onetimeFrequency = ot ? this.optionOf(ot).frequency : '';
      }

      // The plan toggle owns both strings. Queried from the component root rather
      // than walked up from the first input, so it does not depend on where in the
      // card the inputs happen to sit.
      const toggle = this.querySelector('[data-td-bbb2-save-template]');
      this.saveTemplate = (toggle && toggle.getAttribute('data-td-bbb2-save-template')) || '';
      this.planChangedText = (toggle && toggle.getAttribute('data-td-bbb2-plan-changed-text')) || '';
      this.liveRegion = this.querySelector(SELECTORS.liveRegion);

      this.handleCheckboxChange = this.onCheckboxChange.bind(this);
      this.handleOptionChange = this.onOptionChange.bind(this);
      this.handleStackToggle = this.toggleStack.bind(this);
      this.handleStackKeydown = this.onStackKeydown.bind(this);

      this.stackEl = this.querySelector(SELECTORS.stack);
      if (this.stackEl) {
        this.stackToggle = this.stackEl.querySelector(SELECTORS.stackToggle);
        this.stackCount = this.stackEl.querySelector(SELECTORS.stackCount);
        this.stackToggleText = this.stackEl.querySelector(SELECTORS.stackToggleText);
        this.stackWas = this.stackEl.querySelector(SELECTORS.stackWas);
        this.stackNow = this.stackEl.querySelector(SELECTORS.stackNow);
        this.stackSuffix = this.stackEl.querySelector(SELECTORS.stackPerMonth);
        this.stackBadge = this.stackEl.querySelector(SELECTORS.stackBadge);
        this.stackList = this.stackEl.querySelector(SELECTORS.stackList);
        this.baseTitle = this.stackEl.getAttribute('data-td-bbb-base-title') || '';
        this.baseLabel = this.stackEl.getAttribute('data-td-bbb-base-label') || '';
        this.basePrice = this.stackEl.getAttribute('data-td-bbb-base-price-value') || '';
        this.stackShowText = this.stackEl.getAttribute('data-td-bbb-stack-show-text') || "What's included";
        this.stackHideText = this.stackEl.getAttribute('data-td-bbb-stack-hide-text') || 'Hide details';
        this.stackBadgeText = this.stackEl.getAttribute('data-td-bbb-stack-badge-text') || 'Subscribe & Save';
        this.stackDiscountLabel = this.stackEl.getAttribute('data-td-bbb-stack-discount-label') || 'Stack discount';
        this.subDiscountLabel = this.stackEl.getAttribute('data-td-bbb2-stack-subscription-label') || '';
        this.totalLabel = this.stackEl.getAttribute('data-td-bbb2-stack-total-label') || '';
        this.itemSingular = this.stackEl.getAttribute('data-td-bbb2-stack-item-singular') || '';
        this.itemPlural = this.stackEl.getAttribute('data-td-bbb2-stack-item-plural') || '';
        this.appliedText = this.stackEl.getAttribute('data-td-bbb2-stack-applied-text') || '';
        this.unitSingular = this.stackEl.getAttribute('data-td-bbb2-stack-unit-singular') || '';
        this.unitPlural = this.stackEl.getAttribute('data-td-bbb2-stack-unit-plural') || '';
        this.eachText = this.stackEl.getAttribute('data-td-bbb2-stack-each-text') || '';
        this.deliveredText = this.stackEl.getAttribute('data-td-bbb2-stack-delivered-text') || '';
        if (this.stackToggle) {
          this.stackToggle.addEventListener('click', this.handleStackToggle);
          this.stackEl.addEventListener('keydown', this.handleStackKeydown);
        }
      }

      this.checkboxes.forEach((cb) => cb.addEventListener('change', this.handleCheckboxChange));
      this.optionInputs.forEach((input) => input.addEventListener('change', this.handleOptionChange));

      this.resolve();
    }

    /**
     * Disconnects all component event listeners.
     * @returns {void}
     */
    disconnectedCallback() {
      this.checkboxes.forEach((cb) => cb.removeEventListener('change', this.handleCheckboxChange));
      this.optionInputs.forEach((input) => input.removeEventListener('change', this.handleOptionChange));
      if (this.stackToggle) this.stackToggle.removeEventListener('click', this.handleStackToggle);
      if (this.stackEl) this.stackEl.removeEventListener('keydown', this.handleStackKeydown);
    }

    /**
     * Resolves the stack after a booster changes.
     * @returns {void}
     */
    onCheckboxChange() {
      this.resolve();
      this.goToFirstSlide();
    }

    /**
     * Resolves the stack after the purchase option changes.
     * @returns {void}
     */
    onOptionChange() {
      this.resolve();
    }

    /**
     * Parses the server-rendered combo data.
     * @returns {Array<Object>} Normalized combo records.
     */
    parseCombos() {
      const el = this.querySelector(SELECTORS.combos);
      let raw = [];
      if (el) {
        try { raw = JSON.parse(el.textContent.trim()); } catch { raw = []; }
      }
      return raw.map((c) => Object.assign({}, c, {
        keys: String(c.boosterKeys || '').split(',').map((s) => s.trim()).filter(Boolean).sort(),
        frequency: String(c.frequency || ''),
      }));
    }

    /**
     * Gets the sorted keys for selected boosters.
     * @returns {Array<string>} Selected booster keys.
     */
    checkedKeys() {
      return this.checkboxes
        .filter((cb) => cb.checked)
        .map((cb) => cb.getAttribute('data-td-bbb-booster-key'))
        .sort();
    }

    /**
     * Tests whether two sorted key arrays contain the same values.
     * @param {Array<string>} a - First key array.
     * @param {Array<string>} b - Second key array.
     * @returns {boolean} Whether the arrays match.
     */
    sameSet(a, b) {
      return a.length === b.length && a.every((k, i) => k === b[i]);
    }

    /**
     * Finds the combo matching a set of booster keys at one frequency.
     * @param {Array<string>} keys - Sorted booster keys.
     * @param {string} frequency - Frequency handle.
     * @returns {Object|null} Matching combo, when configured.
     */
    findCombo(keys, frequency) {
      return this.combos.find((c) => c.frequency === frequency && this.sameSet(c.keys, keys)) || null;
    }

    /**
     * Gets the checked purchase option, parsed into its plan and frequency halves.
     * Resolved once per pass into this.active, so the rest of a resolve reads a
     * value rather than re-scanning the inputs.
     * @returns {{input: HTMLInputElement|null, plan: string, frequency: string}} Active option.
     */
    activeOption() {
      const checked = this.optionInputs.find((input) => input.checked) || this.optionInputs[0] || null;
      if (!checked) return { input: null, plan: 'onetime', frequency: '' };
      return Object.assign({ input: checked }, this.optionOf(checked));
    }

    /**
     * Checks whether the active option is a subscription.
     * @returns {boolean} Whether a selling plan should be attached.
     */
    isSub() {
      return (this.active || this.activeOption()).plan === 'subscription';
    }

    /**
     * Gets the frequency whose product the active option buys.
     * @returns {string} Active frequency handle.
     */
    activeFrequency() {
      return (this.active || this.activeOption()).frequency;
    }

    /**
     * Reads the plan and frequency an option input represents.
     * @param {HTMLInputElement} input - Option radio.
     * @returns {{plan: string, frequency: string}} That option's halves.
     */
    optionOf(input) {
      return {
        plan: input.getAttribute('data-td-bbb2-plan') || 'onetime',
        frequency: input.getAttribute('data-td-bbb2-frequency') || '',
      };
    }

    /**
     * Resolves the combo an option input would buy.
     * @param {Array<string>} keys - Sorted booster keys.
     * @param {HTMLInputElement} input - Option radio.
     * @returns {Object|null} Combo for that option.
     */
    comboForOption(keys, input) {
      return this.findCombo(keys, this.optionOf(input).frequency);
    }

    /**
     * Tests whether an option is purchasable for the current booster set.
     * @param {Array<string>} keys - Sorted booster keys.
     * @param {HTMLInputElement} input - Option radio.
     * @returns {boolean} Whether the option can be selected.
     */
    optionAvailable(keys, input) {
      const combo = this.comboForOption(keys, input);
      if (!combo || !combo.available) return false;
      // A subscription option needs a Recharge plan on that specific product.
      return this.optionOf(input).plan !== 'subscription' || !!(combo.hasSub && combo.sellingPlanId);
    }

    /**
     * Resolves the selected stack and synchronizes all dependent UI.
     * @returns {void}
     */
    resolve() {
      const keys = this.checkedKeys();
      this.settleOptions(keys);
      this.active = this.activeOption();

      const combo = this.findCombo(keys, this.active.frequency);
      this.currentCombo = combo;

      // Both branches write the form, so it can never describe a product the
      // controls no longer represent — the disabled button is an affordance, not
      // a guard, and the form is what actually posts.
      this.applyPlan(combo);
      if (combo && combo.available) {
        if (this.idInput) this.idInput.value = combo.variantId;
        this.updatePrice(combo);
        this.setAtcDisabled(false);
      } else {
        if (this.idInput) this.idInput.value = '';
        this.updatePrice(null);
        this.setAtcDisabled(true);
      }

      this.updatePlanPrices(keys);
      this.updateDisabledCheckboxes(keys);
      this.updateActiveStyles();
      this.updateGallery();
      this.updateBasePrice();
      this.updateCardPrices();
      this.updateStack(combo);
    }

    // An option is selectable only when its product exists, is in stock, and — for a
    // subscription card — carries a Recharge plan. An option that goes away is
    // greyed and labelled, and a selection that goes away falls back to the first
    // option still available, so the picker never sits on an unbuyable card.
    /**
     * Applies purchase-option availability for the current booster set.
     * @param {Array<string>} keys - Currently selected booster keys.
     * @returns {void}
     */
    settleOptions(keys) {
      const available = [];
      this.optionInputs.forEach((input) => {
        const ok = this.optionAvailable(keys, input);
        input.disabled = !ok;
        const card = this.optionCards.get(input);
        if (card) card.classList.toggle('is-unavailable', !ok);
        const soldOut = card ? card.querySelector(SELECTORS.planSoldOut) : null;
        if (soldOut) soldOut.setAttribute('aria-hidden', String(ok));
        if (ok) available.push(input);
      });

      const checked = this.optionInputs.find((input) => input.checked);
      if ((checked && !checked.disabled) || !available.length) return;

      // Prefer an option on the plan the shopper chose. Falling back on DOM order
      // alone would move a One-Time buyer onto a subscription, because every
      // subscription card renders before the One-Time one.
      const was = checked ? this.optionOf(checked) : null;
      const next = available.find((input) => this.optionOf(input).plan === (was && was.plan)) || available[0];
      next.checked = true;
      const now = this.optionOf(next);
      if (was && (now.plan !== was.plan || now.frequency !== was.frequency)) this.announcePlanChange(next);
    }

    /**
     * Announces a fallback that changed the purchase plan, not just the supply
     * length — a switch between one-time and recurring is not a silent change.
     * @param {HTMLInputElement} input - The option now selected.
     * @returns {void}
     */
    announcePlanChange(input) {
      if (!this.liveRegion) return;
      const card = this.optionCards.get(input);
      const title = card ? card.querySelector(SELECTORS.planTitle) : null;
      if (title) this.liveRegion.textContent = this.planChangedText.replace(PLAN_TOKEN, title.textContent.trim());
    }

    // selling_plan input present only for a subscription.
    /**
     * Synchronizes the Recharge selling-plan form input.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    applyPlan(combo) {
      if (!this.form) return;
      let sp = this.form.querySelector(SELECTORS.sellingPlanInput);
      const wantSub = !!combo && combo.available && this.isSub() && combo.hasSub && combo.sellingPlanId;
      if (wantSub) {
        if (!sp) {
          sp = document.createElement('input');
          sp.type = 'hidden';
          sp.name = 'selling_plan';
          this.form.appendChild(sp);
        }
        sp.value = combo.sellingPlanId;
      } else if (sp) {
        sp.remove();
      }
    }

    /**
     * Updates the active add-to-cart price.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    updatePrice(combo) {
      if (!combo) {
        if (this.atcPriceEl) this.atcPriceEl.textContent = '';
        if (this.atcCompareEl) this.atcCompareEl.textContent = '';
        return;
      }
      const sub = this.isSub() && combo.hasSub;
      const priceMoney = sub ? combo.subMoney : combo.regMoney;
      const priceCents = sub ? combo.subCents : combo.regCents;
      const compareCents = this.comparisonCents(combo, this.activeFrequency());
      if (this.atcPriceEl) this.atcPriceEl.textContent = priceMoney;
      if (this.atcCompareEl) {
        this.atcCompareEl.textContent = compareCents > priceCents ? this.formatMoney(compareCents, priceMoney) : '';
      }
    }

    /**
     * Sets text and visibility on an optional element.
     * @param {Element|null} el - Target element.
     * @param {boolean} show - Whether the value is visible.
     * @param {string} val - Text value.
     * @returns {void}
     */
    setOptionalText(el, show, val) {
      if (!el) return;
      el.textContent = show ? val : '';
      el.hidden = !show;
    }

    /**
     * Updates every option card: savings, struck total, headline total, per-unit line.
     * The headline is the ORDER TOTAL so it tracks what is actually charged; the
     * per-unit price sits under it as the comparison figure between supply lengths.
     * @param {Array<string>} keys - Currently selected booster keys.
     * @returns {void}
     */
    updatePlanPrices(keys) {
      this.optionInputs.forEach((input) => {
        const card = this.optionCards.get(input);
        if (!card) return;
        const combo = this.comboForOption(keys, input);
        const sub = this.optionOf(input).plan === 'subscription' && !!(combo && combo.hasSub);

        let nowMoney = '';
        let wasMoney = '';
        let unitMoney = '';
        let saveCents = 0;
        let fromCents = 0;

        if (combo) {
          nowMoney = sub ? combo.subMoney : combo.regMoney;
          unitMoney = sub ? combo.subUnitMoney : combo.regUnitMoney;
          fromCents = this.comparisonCents(combo, this.optionOf(input).frequency);
          wasMoney = this.formatMoney(fromCents, nowMoney);
          saveCents = fromCents - (sub ? combo.subCents : combo.regCents);
        }
        const showWas = !!combo && saveCents > 0;

        this.setOptionalText(card.querySelector(SELECTORS.planNow), !!combo, nowMoney);
        this.setOptionalText(card.querySelector(SELECTORS.planWas), showWas, wasMoney);

        const showSave = showWas && fromCents > 0 && this.saveTemplate !== '';
        const pct = showSave ? Math.round((saveCents / fromCents) * 100) : 0;
        this.setOptionalText(
          card.querySelector(SELECTORS.planSave),
          showSave && pct > 0,
          this.saveTemplate.replace(SAVINGS_TOKEN, pct + '%')
        );

        // The per-unit line needs products_per_delivery on the combo product.
        const unit = card.querySelector(SELECTORS.planUnit);
        const unitEl = card.querySelector(SELECTORS.planUnitMoney);
        const showUnit = !!combo && combo.units > 0;
        if (unitEl) unitEl.textContent = showUnit ? unitMoney : '';
        if (unit) unit.hidden = !showUnit;
      });
    }

    // Two different numbers on purpose. The base row SHOWS a static one-time price so
    // it never jumps when the plan changes, while `this.basePrice` stays plan- and
    // frequency-aware because the stack itemization has to sum to the charged total.
    /**
     * Updates the displayed base price and the base price used by the stack rows.
     * @returns {void}
     */
    updateBasePrice() {
      const displayBase = this.findCombo([], this.onetimeFrequency);
      if (displayBase) {
        if (this.basePriceEl) this.basePriceEl.textContent = displayBase.regMoney;
        this.setOptionalText(this.baseWasEl, displayBase.compareCents > displayBase.regCents, displayBase.compareMoney);
      }

      const base = this.findCombo([], this.activeFrequency());
      // Blank rather than return: leaving the previous frequency's figure feeds a
      // wrong number straight into the stack's first row.
      this.basePrice = base ? base.regMoney : '';
    }

    // Formats cents using a server-rendered money string as the template.
    /**
     * Formats cents using a server-rendered money value as the template.
     * @param {number} cents - Amount in cents.
     * @param {string} sample - Server-rendered money string.
     * @returns {string} Formatted money value.
     */
    formatMoney(cents, sample) {
      // Always two decimals, unlike V1, which trims a trailing ".00". V2 puts these
      // figures in a column beside server-rendered `| money` strings, which never
      // trim, so "$60" next to "$237.60" would read as a formatting bug.
      const num = (cents / 100).toFixed(2);
      return String(sample || '$0').replace(/[\d.,]+/, num);
    }

    // Same split as updateBasePrice: the visible "+$29.99" is a static one-time
    // delta so stacking reads the same on every plan, while the value handed to the
    // stack rows stays plan- and frequency-aware so the itemization reconciles.
    /**
     * Updates displayed booster deltas and the deltas used by the stack rows.
     * @returns {void}
     */
    updateCardPrices() {
      const displayBase = this.findCombo([], this.onetimeFrequency);
      const frequency = this.activeFrequency();
      const base = this.findCombo([], frequency);

      this.checkboxes.forEach((cb) => {
        const key = cb.getAttribute('data-td-bbb-booster-key');
        const card = cb.closest(SELECTORS.card);
        const el = card ? card.querySelector(SELECTORS.autoPrice) : null;

        if (el) {
          const displaySolo = this.findCombo([key], this.onetimeFrequency);
          let displayText = '';
          if (displayBase && displaySolo) {
            const displayCents = displaySolo.regCents - displayBase.regCents;
            if (displayCents > 0) displayText = '+' + this.formatMoney(displayCents, displayBase.regMoney);
          }
          el.textContent = displayText;
        }

        // Always recomputed, including where a merchant set price_label. That
        // label still shows on the card — it is rendered server-side as a static
        // span — but the stack row must stay frequency-aware or the column stops
        // summing to the charged total.
        const solo = base ? this.findCombo([key], frequency) : null;
        if (!solo) {
          cb.removeAttribute('data-td-bbb-booster-price');
          return;
        }
        const rowCents = solo.regCents - base.regCents;
        if (rowCents <= 0) {
          cb.removeAttribute('data-td-bbb-booster-price');
          return;
        }
        cb.setAttribute('data-td-bbb-booster-price', this.formatMoney(rowCents, base.regMoney));
      });
    }

    /**
     * Sets the add-to-cart availability state.
     * @param {boolean} disabled - Whether purchasing is disabled.
     * @returns {void}
     */
    setAtcDisabled(disabled) {
      if (!this.atcButton) return;
      this.atcButton.disabled = disabled;
      this.atcButton.classList.toggle('is-unavailable', disabled);
    }

    // Disable a booster when its target combo is missing or unavailable at the active
    // frequency. A booster is sold out when no in-stock combo contains it at any
    // frequency, independent of the current stack.
    /**
     * Updates checkbox availability for the current stack.
     * @param {Array<string>} keys - Currently selected booster keys.
     * @returns {void}
     */
    updateDisabledCheckboxes(keys) {
      const frequency = this.activeFrequency();
      this.checkboxes.forEach((cb) => {
        const k = cb.getAttribute('data-td-bbb-booster-key');
        const next = cb.checked ? keys.filter((x) => x !== k) : keys.concat(k).sort();
        const nextCombo = this.findCombo(next, frequency);
        const ok = !!(nextCombo && nextCombo.available);
        const soldOut = !this.combos.some((combo) => combo.available && combo.keys.includes(k));
        // A checked booster always stays removable. Disabling it would trap the
        // shopper whenever the smaller set has no combo at this frequency.
        cb.disabled = cb.checked ? false : !ok;
        const card = cb.closest(SELECTORS.card);
        const shell = cb.closest(SELECTORS.cardShell);
        const soldOutLabel = card ? card.querySelector(SELECTORS.soldOutLabel) : null;
        if (card) {
          card.classList.toggle('is-disabled', !ok);
          card.classList.toggle('is-sold-out', soldOut);
        }
        if (shell) shell.classList.toggle('is-sold-out', soldOut);
        if (soldOutLabel) soldOutLabel.setAttribute('aria-hidden', String(!soldOut));
      });
    }

    /**
     * Synchronizes visual selected states.
     * @returns {void}
     */
    updateActiveStyles() {
      this.checkboxes.forEach((cb) => {
        const card = cb.closest(SELECTORS.card);
        if (card) card.classList.toggle('td-active', cb.checked);
      });
    }

    /**
     * Returns the gallery to its generated stack slide.
     * @returns {void}
     */
    goToFirstSlide() {
      // Swiper requires its own data attributes, so this selector is the approved library exception.
      const main = this.querySelector(SELECTORS.mainSwiper);
      if (main && main.swiper) main.swiper.slideTo(0);
    }

    /**
     * Rebuilds the selected-booster gallery composite.
     * @returns {void}
     */
    updateGallery() {
      if (!this.boosterContainer) return;
      // Guarded on the booster set: resolve() also runs on plan changes, and
      // rebuilding here would destroy and re-decode the hero images on every
      // plan click, for no change in what they show.
      const signature = this.checkedKeys().join(',');
      if (signature === this.gallerySignature) return;
      this.gallerySignature = signature;

      this.boosterContainer.textContent = '';
      this.checkboxes.filter((cb) => cb.checked).forEach((cb) => {
        const src = cb.getAttribute('data-td-bbb-booster-image');
        if (!src) return;
        const plus = document.createElement('span');
        plus.className = 'td-bbb__preview-plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        const img = document.createElement('img');
        img.className = 'td-bbb__preview-booster';
        img.src = src;
        img.srcset = cb.getAttribute('data-td-bbb-booster-image-srcset') || '';
        img.sizes = GALLERY_IMAGE_SIZES;
        // Intrinsic dimensions, or the composite lays out at zero and jumps once
        // each image decodes.
        img.width = GALLERY_IMAGE_WIDTH;
        img.height = GALLERY_IMAGE_HEIGHT;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        this.boosterContainer.appendChild(plus);
        this.boosterContainer.appendChild(img);
      });
    }

    /**
     * Sets the stack itemization panel state.
     * @param {boolean} open - Whether the panel is expanded.
     * @returns {void}
     */
    setStackOpen(open) {
      if (!this.stackToggle) return;
      this.stackToggle.setAttribute('aria-expanded', String(open));
      if (this.stackList) this.stackList.hidden = !open;
      if (this.stackToggleText) this.stackToggleText.textContent = open ? this.stackHideText : this.stackShowText;
    }

    /**
     * Toggles the stack itemization panel.
     * @returns {void}
     */
    toggleStack() {
      if (!this.stackToggle) return;
      this.setStackOpen(this.stackToggle.getAttribute('aria-expanded') !== 'true');
    }

    /**
     * Collapses the stack itemization panel on Escape.
     * @param {KeyboardEvent} event - Originating keydown event.
     * @returns {void}
     */
    onStackKeydown(event) {
      if (event.key !== 'Escape' || !this.stackToggle) return;
      if (this.stackToggle.getAttribute('aria-expanded') !== 'true') return;
      this.setStackOpen(false);
      this.stackToggle.focus();
    }

    /**
     * Updates stack totals and itemization.
     * @param {Object|null} combo - Active combo record.
     * @returns {void}
     */
    updateStack(combo) {
      if (!this.stackEl) return;
      if (!combo) {
        // Blank rather than return: an early return leaves the previous stack's
        // prices on screen next to a disabled button.
        if (this.stackList) this.stackList.textContent = '';
        if (this.stackNow) this.stackNow.textContent = '';
        this.setOptionalText(this.stackWas, false, '');
        this.setOptionalText(this.stackSuffix, false, '');
        this.setOptionalText(this.stackBadge, false, '');
        return;
      }
      const count = 1 + this.checkedKeys().length;
      const itemWord = count === 1 ? this.itemSingular : this.itemPlural;
      if (this.stackCount) this.stackCount.textContent = (count + ' ' + itemWord).trim();

      const sub = this.isSub() && combo.hasSub;
      const nowMoney = sub ? combo.subMoney : combo.regMoney;
      const nowCents = sub ? combo.subCents : combo.regCents;
      const wasCents = this.comparisonCents(combo, this.activeFrequency());
      const wasMoney = this.formatMoney(wasCents, nowMoney);

      if (this.stackNow) this.stackNow.textContent = nowMoney;
      this.setOptionalText(this.stackWas, wasCents > nowCents, wasMoney);

      // The suffix is the active frequency's own, so a 3-month supply never reads "/mo".
      const activeInput = this.optionInputs.find((input) => input.checked);
      const suffix = activeInput ? activeInput.getAttribute('data-td-bbb2-freq-suffix') || '' : '';
      this.setOptionalText(this.stackSuffix, sub && suffix !== '', suffix);

      // Deliberately NOT the struck price's percentage. This badge names what
      // subscribing is worth; the struck price also carries the stack discount, so
      // measuring the badge against it would advertise a subscription saving that
      // the shopper gets partly for stacking.
      let pct = 0;
      if (sub && combo.regCents > combo.subCents) {
        pct = Math.round(((combo.regCents - combo.subCents) / combo.regCents) * 100);
      }
      this.setOptionalText(this.stackBadge, sub && pct > 0, [this.stackBadgeText, pct + '%', this.appliedText].filter(Boolean).join(' '));

      this.renderStackList(combo, sub);
    }

    /**
     * Sum of every item in the stack at its own regular price for one frequency —
     * the base plus each checked booster's solo delta. This is the figure the
     * itemization visibly adds up to.
     * @param {string} frequency - Frequency handle to price against.
     * @returns {number} Regular parts sum in cents.
     */
    regularPartsCents(frequency) {
      const base = this.findCombo([], frequency);
      if (!base) return 0;
      return this.checkedKeys().reduce((sum, key) => {
        const solo = this.findCombo([key], frequency);
        return solo && solo.regCents > base.regCents ? sum + (solo.regCents - base.regCents) : sum;
      }, base.regCents);
    }

    /**
     * The figure every struck price is struck from: what the shopper would pay
     * buying these items separately.
     *
     * `subCompareCents` is deliberately not consulted. It is the combo product's
     * own price, which sits mid-chain — after the stack discount, before the
     * subscription one — so the subscription branch used to strike from a number
     * $70 below the one the itemization closes on, putting two different "was"
     * figures on the same screen. compare_at_price is set to the parts sum on every
     * combo product today; the computed sum is the source of truth so a combo that
     * loses its compare-at still strikes correctly.
     * @param {Object} combo - Active combo record.
     * @param {string} frequency - Frequency handle to price against.
     * @returns {number} Comparison amount in cents.
     */
    comparisonCents(combo, frequency) {
      return Math.max(this.regularPartsCents(frequency), combo.compareCents || 0);
    }

    /**
     * Parses a formatted money value into cents.
     * @param {string} money - Formatted money value.
     * @returns {number} Parsed amount in cents.
     */
    parseCents(money) {
      return Math.round(parseFloat(String(money).replace(/[^0-9.]/g, '')) * 100) || 0;
    }

    /**
     * Rebuilds the expanded stack itemization.
     * @param {Object} combo - Active combo record.
     * @param {boolean} sub - Whether subscription pricing is active.
     * @returns {void}
     */
    renderStackList(combo, sub) {
      if (!this.stackList) return;
      this.stackList.textContent = '';
      /**
       * Appends one itemization row.
       * @param {string} name - Row name.
       * @param {string} label - Optional row label.
       * @param {string} labelColor - Optional label color.
       * @param {string} price - Formatted row price.
       * @param {boolean} isSave - Whether this is a discount row.
       * @returns {void}
       */
      const addRow = (name, label, labelColor, price, isSave) => {
        const r = document.createElement('div');
        r.className = 'td-bbb__stack-row' + (isSave ? ' td-bbb__stack-row--save' : '');
        const left = document.createElement('span');
        left.className = 'td-bbb__stack-row-left';
        const n = document.createElement('span');
        n.className = 'td-bbb__stack-row-name';
        n.textContent = name;
        left.appendChild(n);
        if (label) {
          const l = document.createElement('span');
          l.className = 'td-bbb__stack-row-label';
          if (labelColor) l.style.color = labelColor;
          l.textContent = label;
          left.appendChild(l);
        }
        const p = document.createElement('span');
        p.className = 'td-bbb__stack-row-price';
        p.textContent = price;
        r.appendChild(left);
        r.appendChild(p);
        this.stackList.appendChild(r);
      };

      addRow(this.baseTitle, this.baseLabel, '', this.basePrice, false);
      let rowSum = this.parseCents(this.basePrice);
      this.checkboxes.filter((cb) => cb.checked).forEach((cb) => {
        const name = cb.getAttribute('data-td-bbb-booster-name') || '';
        const cat = cb.getAttribute('data-td-bbb-booster-category') || '';
        const accent = cb.getAttribute('data-td-bbb-booster-accent') || '';
        const price = (cb.getAttribute('data-td-bbb-booster-price') || '').replace(/^\+\s*/, '');
        rowSum += this.parseCents(price);
        addRow(name, cat, accent, price, false);
      });
      // Rows are regular prices, so the two savings are itemized separately and the
      // column still reconciles: regular parts − stack discount − subscription
      // discount = the charged total.
      const stackDiscount = rowSum - combo.regCents;
      if (stackDiscount > 0) {
        addRow(this.stackDiscountLabel, '', '', '−' + this.formatMoney(stackDiscount, combo.regMoney), true);
      }
      const subDiscount = sub ? combo.regCents - combo.subCents : 0;
      if (subDiscount > 0 && this.subDiscountLabel) {
        addRow(this.subDiscountLabel, '', '', '−' + this.formatMoney(subDiscount, combo.regMoney), true);
      }

      // Closes the column. The struck figure is the regular parts sum — the number
      // the rows above actually add up to — rather than the combo's own compare
      // price, which appears nowhere in the itemization.
      const totalCents = sub ? combo.subCents : combo.regCents;
      this.addStackTotal(rowSum, totalCents, combo.regMoney);

      this.renderStackNote(combo);
    }

    /**
     * Appends the closing total row, struck compare included.
     * @param {number} fromCents - Regular parts sum shown above.
     * @param {number} totalCents - Amount actually charged.
     * @param {string} sample - Server-rendered money string, for formatting.
     * @returns {void}
     */
    addStackTotal(fromCents, totalCents, sample) {
      if (!this.stackList || !this.totalLabel) return;
      const row = document.createElement('div');
      row.className = 'td-bbb__stack-row td-bbb__stack-row--total';

      const left = document.createElement('span');
      left.className = 'td-bbb__stack-row-left';
      const name = document.createElement('span');
      name.className = 'td-bbb__stack-row-name';
      name.textContent = this.totalLabel;
      left.appendChild(name);

      const price = document.createElement('span');
      price.className = 'td-bbb__stack-row-price';
      if (fromCents > totalCents) {
        const was = document.createElement('span');
        was.className = 'td-bbb__stack-total-was';
        was.textContent = this.formatMoney(fromCents, sample);
        price.appendChild(was);
      }
      const now = document.createElement('span');
      now.className = 'td-bbb__stack-total-now';
      now.textContent = this.formatMoney(totalCents, sample);
      price.appendChild(now);

      row.appendChild(left);
      row.appendChild(price);
      this.stackList.appendChild(row);
    }

    /**
     * Appends the "N bottles of each delivered once a month" line under the
     * itemization, so the quantity and the cadence are both stated outright.
     * @param {Object} combo - Active combo record.
     * @returns {void}
     */
    renderStackNote(combo) {
      if (!this.stackList) return;
      const itemCount = 1 + this.checkedKeys().length;
      // combo.units counts every bottle in the stack, so per-item is units / items.
      // products_per_delivery is merchant data and nothing enforces that it divides
      // evenly; rounding an uneven split would state a confident, wrong number, so
      // the line is dropped instead.
      if (combo.units < 1 || combo.units % itemCount !== 0) return;
      const perItem = combo.units / itemCount;

      const active = this.optionInputs.find((input) => input.checked);
      const phrase = active ? active.getAttribute('data-td-bbb2-delivery-phrase') || '' : '';

      const parts = [
        String(perItem),
        perItem === 1 ? this.unitSingular : this.unitPlural,
        itemCount > 1 ? this.eachText : '',
        phrase ? this.deliveredText : '',
        phrase,
      ].filter(Boolean);
      if (parts.length < 2) return;

      const note = document.createElement('p');
      note.className = 'td-bbb__stack-note';
      note.textContent = parts.join(' ');
      this.stackList.appendChild(note);
    }
  }
  customElements.define('td-booster-buybox-v2-builder', TdBoosterBuyboxV2Builder);
}
